import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import './CodeCompiler.css';
import {
  algorithmProblems,
  classDesignProblems,
  fullstackProblems,
  agentProblems,
  type Problem,
  type AlgorithmProblem,
  type ClassDesignProblem,
  type FullstackProblem,
  type ProblemFile,
} from '../data/problems';
import Coach, { type CoachState } from '../components/Coach';
import { type TestResult } from '../utils/codeRunner';
import { resolveProblem } from '../utils/resolveProblem';
import { usePyodideWorker } from './usePyodideWorker';
import { useJsWorker } from './useJsWorker';
import { useIdeTimer } from './useIdeTimer';
import IdeLanguageTabs from './IdeLanguageTabs';
import IdeTestPanel from './IdeTestPanel';
import { findProblemById } from '../data/problems';
import { findReactProblemById } from '../data/problems/_loader';
import ReactProblemPane from './ReactProblemPane';
import { Markdown } from '../lib/markdown';
import { useAuth, useIsAdmin } from '../auth/AuthContext';
import { useProblemStore } from '../context/ProblemStoreProvider';
import ProblemEditorDrawer from './ProblemEditorDrawer';
import { useUser } from '../context/UserContext';
import { api } from '../auth/api';
import {
  hydrateFiles,
  saveCode as persistCode,
  clearCode,
  markSolved,
  recordXpEvent,
} from '../lib/progress';
import { track } from '../lib/analytics';
import SplitPane from './SplitPane';
import { useIsMobile } from '../lib/useIsMobile';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import type { editor as MonacoEditor } from 'monaco-editor';

type Language = 'javascript' | 'typescript' | 'python';
type TestTab = 'tests' | 'console';

// Test result + PyOutput types live with the worker hook now; re-imported above.

// ─── Coach message ────────────────────────────────────────────────────────────

function deriveCoachState(
  results: TestResult[],
  running: boolean,
  consecutiveFails: number,
  pyError?: string,
): CoachState {
  if (running) return { kind: 'running' };
  if (results.length === 0) {
    if (pyError) return { kind: 'error', message: pyError };
    return { kind: 'idle' };
  }
  const passCount = results.filter(r => r.passed).length;
  const totalCount = results.length;
  if (passCount === totalCount) return { kind: 'all-pass', passCount };
  return { kind: 'partial-fail', passCount, totalCount, consecutiveFails };
}

// ─── Timer ───────────────────────────────────────────────────────────────────

function formatTime(secs: number): string {
  const m = Math.floor(secs / 60).toString().padStart(2, '0');
  const s = (secs % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

// The algorithm/class-design IDE. Renamed from `CodeCompiler` so the
// exported `CodeCompiler` below can stay a thin router that picks between
// this and `ReactProblemPane` BEFORE any hooks fire — hooks-rules require
// the early branch to happen above all useState/useEffect calls.
const AlgoOrClassCompiler: React.FC = () => {
  const { problemId } = useParams<{ problemId: string }>();
  const navigate = useNavigate();

  const initialProblem = resolveProblem(problemId);

  // FullstackProblem has no single-file pyStarter/jsStarter — it's always
  // multi-file via `files`, so this path is only reached for algorithm/class.
  const starterFor = (problem: Problem, lang: Language): string => {
    const p = problem as { pyStarter?: string; jsStarter?: string; tsStarter?: string };
    if (lang === 'python') return p.pyStarter ?? '';
    if (lang === 'typescript') return p.tsStarter ?? p.jsStarter ?? '';
    return p.jsStarter ?? '';
  };

  const isLangAllowed = (problem: Problem, lang: Language): boolean => {
    if (problem.supportedLanguages) return problem.supportedLanguages.includes(lang);
    // For problems without an explicit allowlist, TypeScript piggybacks on the
    // JS path (strip-types → run as JS), so any JS-capable problem is also TS.
    return true;
  };

  // Picks the language to open a problem in: python when allowed (the historic
  // default), otherwise the first supported language. Matters for backend:'node'
  // full-stack problems, which only expose javascript.
  const firstAllowedLang = (problem: Problem): Language => {
    const order: Language[] = ['python', 'javascript', 'typescript'];
    return order.find(l => isLangAllowed(problem, l)) ?? 'python';
  };

  // Editor file state. Single-file problems synthesize a 1-file array from
  // pyStarter / jsStarter / tsStarter; multi-file problems use their `files` array.
  // Entry file (where the user's solution lives) is the last entry.
  type EditorFile = { name: string; code: string; readonly?: boolean; isEntry?: boolean };

  const filesFromProblem = (problem: Problem, lang: Language): EditorFile[] => {
    // `files` shape varies by problem type (fullstack omits `typescript` and makes
    // python/javascript optional), so read it through a permissive view.
    const bundle = problem.files as
      | { python?: ProblemFile[]; javascript?: ProblemFile[]; typescript?: ProblemFile[] }
      | undefined;
    let langFiles: ProblemFile[] | undefined;
    if (bundle) {
      if (lang === 'python') langFiles = bundle.python;
      else if (lang === 'typescript') langFiles = bundle.typescript ?? bundle.javascript;
      else langFiles = bundle.javascript;
    }
    if (langFiles && langFiles.length > 0) {
      return langFiles.map(f => ({ name: f.name, code: f.code, readonly: f.readonly, isEntry: f.isEntry }));
    }
    const name = lang === 'python' ? 'solution.py' : lang === 'typescript' ? 'solution.ts' : 'solution.js';
    return [{ name, code: starterFor(problem, lang), isEntry: true }];
  };

  const entryFileName = (fs: EditorFile[]): string => {
    const entry = fs.find(f => f.isEntry) ?? fs[fs.length - 1];
    return entry?.name ?? '';
  };

  const [language, setLanguage]         = useState<Language>(() => firstAllowedLang(initialProblem));
  const [problemKey, setProblemKey]     = useState(() => problemId ?? 'algo-1');
  const [selectedProblem, setSelected]  = useState<Problem>(initialProblem);
  useDocumentTitle(selectedProblem.title);
  const initialLang = firstAllowedLang(initialProblem);
  const starterInitialFiles = filesFromProblem(initialProblem, initialLang);
  const hydratedInitialFiles = hydrateFiles(initialProblem.id, initialLang, starterInitialFiles);
  const [files, setFiles]               = useState<EditorFile[]>(hydratedInitialFiles);
  const [activeFile, setActiveFile]     = useState<string>(entryFileName(hydratedInitialFiles));

  // `code` is the active file's content. `setCode` updates the active file
  // in place AND schedules a debounced localStorage write so refreshing or
  // navigating away doesn't lose typed code.
  const code = files.find(f => f.name === activeFile)?.code ?? '';
  const activeReadonly = files.find(f => f.name === activeFile)?.readonly === true;
  const saveTimerRef = useRef<number | null>(null);
  const setCode = (newCode: string) => {
    setFiles(prev => {
      const next = prev.map(f => f.name === activeFile ? { ...f, code: newCode } : f);
      if (saveTimerRef.current !== null) window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = window.setTimeout(() => {
        persistCode(selectedProblem.id, language, next);
      }, 400);
      return next;
    });
  };
  const editorRef = useRef<MonacoEditor.IStandaloneCodeEditor | null>(null);
  // Pyodide worker + test/output state lives in usePyodideWorker. We re-expose
  // the bits we need locally so the rest of the file can keep using `results`
  // / `running` / `pyOutput` as before.
  const py = usePyodideWorker();
  const jsWorker = useJsWorker();
  const results = py.results;
  const setResults = py.setResults;
  const running = py.running;
  const setRunning = py.setRunning;
  const pyOutput = py.output;
  const setPyOutput = py.setOutput;
  const pyLoading = py.loading;
  const [expandedCase, setExpanded]     = useState<number | null>(null);
  const [customInput, setCustomInput]   = useState('');
  const [customResult, setCustomResult] = useState('');
  const [testTab, setTestTab]           = useState<TestTab>('tests');
  const [hintsRevealed, setHintsRevealed] = useState(0);
  const [solutionRevealed, setSolutionRevealed] = useState(false);
  const elapsedSecs = useIdeTimer(problemId);

  // ── Admin authoring (gated by useIsAdmin) ──────────────────────────────────
  const isAdmin = useIsAdmin();
  const { deleteProblem } = useProblemStore();
  const [editorOpen, setEditorOpen] = useState(false);

  const applyProblem = useCallback((problem: Problem, lang: Language) => {
    setSelected(problem);
    const newFiles = filesFromProblem(problem, lang);
    const hydrated = hydrateFiles(problem.id, lang, newFiles);
    setFiles(hydrated);
    setActiveFile(entryFileName(hydrated));
    setResults([]); setPyOutput(null); setCustomResult(''); setExpanded(null);
    setHintsRevealed(0);
    setSolutionRevealed(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleProblemSaved = useCallback((saved: Problem) => {
    setEditorOpen(false);
    // Editing the id is effectively a rename — route to the new key. Otherwise
    // re-apply in place so the prompt reflects the edit immediately.
    if (saved.id !== selectedProblem.id) navigate(`/problem/${saved.id}`);
    else applyProblem(saved, language);
  }, [selectedProblem.id, language, navigate, applyProblem]);

  const handleProblemDelete = useCallback(async () => {
    if (!window.confirm(`Delete problem "${selectedProblem.id}"? This is permanent.`)) return;
    try {
      await deleteProblem(selectedProblem.id);
      navigate('/');
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Delete failed.');
    }
  }, [selectedProblem.id, deleteProblem, navigate]);

  // Sync with URL param changes (e.g., user navigates between problems).
  // If the new problem doesn't support the current language, fall back to one it does.
  useEffect(() => {
    const problem = resolveProblem(problemId);
    const lang: Language = isLangAllowed(problem, language) ? language : firstAllowedLang(problem);
    if (lang !== language) setLanguage(lang);
    applyProblem(problem, lang);
    setProblemKey(problemId ?? 'algo-1');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problemId]);

  // ─── Analytics: problem_open / problem_close (dwell + runs + solved) ─────
  // Lives alongside the URL-sync effect above but separated so its cleanup
  // semantics (fire close on problemId change OR unmount) stay isolated. The
  // run + solve refs are reset here so each open is a clean slate.
  const runsRef = useRef(0);
  const solvedRef = useRef(false);
  useEffect(() => {
    if (!problemId) return;
    const problem = resolveProblem(problemId);
    const openTs = Date.now();
    runsRef.current = 0;
    solvedRef.current = false;
    const tier =
      problem.difficulty === 'Easy' ? 1 :
      problem.difficulty === 'Medium' ? 2 : 3;
    track('problem_open', {
      problem: problem.id,
      skill: problem.skillId,
      tier,
      difficulty: problem.difficulty,
    });
    return () => {
      track('problem_close', {
        problem: problem.id,
        dwell_ms: Date.now() - openTs,
        runs: runsRef.current,
        solved: solvedRef.current,
      });
    };
  }, [problemId]);

  // ─── Analytics: problem_run (fires when a test run completes) ────────────
  // Track the transition running → !running with results present. `passed`
  // count + total is enough to derive pass-rate at the dashboard layer; we
  // intentionally don't send per-case detail (it's user code state, not
  // user-meaningful).
  const wasRunningRef = useRef(false);
  useEffect(() => {
    if (wasRunningRef.current && !running && results.length > 0) {
      const passed = results.filter(r => r.passed).length;
      runsRef.current += 1;
      track('problem_run', {
        problem: selectedProblem.id,
        lang: language,
        result: passed === results.length ? 'pass' : 'fail',
        passed,
        total: results.length,
      });
    }
    wasRunningRef.current = running;
  }, [running, results, selectedProblem.id, language]);

  // Mark "solved" the moment all visible tests pass — that's the moment we
  // care about for stuck-analysis, regardless of whether the user clicks
  // Submit afterward. Used by the cleanup on `problem_close`.
  useEffect(() => {
    if (results.length > 0 && results.every(r => r.passed)) {
      solvedRef.current = true;
    }
  }, [results]);

  const handleLanguageChange = (lang: Language) => {
    setLanguage(lang);
    const newFiles = filesFromProblem(selectedProblem, lang);
    const hydrated = hydrateFiles(selectedProblem.id, lang, newFiles);
    setFiles(hydrated);
    setActiveFile(entryFileName(hydrated));
    setResults([]); setPyOutput(null); setCustomResult(''); setExpanded(null);
  };

  const handleProblemChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const key = e.target.value;
    const problem = findProblemById(key);
    if (!problem) return;
    setProblemKey(key);
    applyProblem(problem, language);
  };

  // JS/TS test runner — dispatched to a Web Worker so an infinite loop in user
  // code can be killed (5s deadline) instead of freezing the tab, and so the
  // code runs sandboxed off the main thread. `language` is narrowed to the
  // worker's runnable union (python never reaches here — runPython handles it).
  const runTests = useCallback(async () => {
    if (language === 'python') return;
    setRunning(true); setResults([]); setPyOutput(null); setExpanded(null);
    const fileArgs = files.map(f => ({ name: f.name, code: f.code }));
    const outcome = selectedProblem.type === 'fullstack'
      ? await jsWorker.runFullstack(selectedProblem as FullstackProblem, fileArgs)
      : await jsWorker.runSuite(selectedProblem as AlgorithmProblem | ClassDesignProblem, fileArgs, language);
    if (outcome.kind === 'timeout') {
      setPyOutput({ stdout: '', stderr: '', error: 'Execution timed out (5s). Possible infinite loop — check your loop conditions.' });
    } else if (outcome.kind === 'error') {
      setPyOutput({ stdout: '', stderr: '', error: outcome.error });
    } else {
      setResults(outcome.results);
      setTestTab('tests');
    }
    setRunning(false);
  }, [files, selectedProblem, language, jsWorker, setRunning, setResults, setPyOutput]);

  // Python test runner — delegates to the worker hook. We additionally reset
  // the expanded test-case index and force the test tab to surface so a new
  // run feels responsive even if results arrive a beat later.
  const runPython = useCallback(() => {
    setExpanded(null);
    setTestTab('tests');
    py.runPython({
      files: files.map(f => ({ name: f.name, code: f.code })),
      problem: selectedProblem,
    });
  }, [files, selectedProblem, py]);

  const runCustomTest = useCallback(async () => {
    if (language === 'python') return;
    // Validate JSON on the main thread so a syntax error is instant; the run
    // itself goes through the worker so a bad loop in the custom input can't
    // freeze the tab either.
    try {
      JSON.parse(customInput);
    } catch {
      setCustomResult('Error: input must be valid JSON');
      return;
    }
    setCustomResult('Running…');
    const outcome = await jsWorker.runCustom(code, customInput, language);
    if (outcome.kind === 'timeout') {
      setCustomResult('Error: timed out (5s). Possible infinite loop.');
    } else if (outcome.kind === 'error') {
      setCustomResult(`Error: ${outcome.error}`);
    } else {
      setCustomResult(outcome.output);
    }
  }, [code, customInput, language, jsWorker]);

  const passCount = results.filter(r => r.passed).length;
  const totalCount = results.length;
  const allPass = totalCount > 0 && passCount === totalCount;
  const pyDisabled = language === 'python' && pyLoading;

  // ─── Submit (Phase 4 — persists solve to backend) ───────────────────────────
  const { state: authState } = useAuth();
  const { refresh: refreshUser } = useUser();
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState('');

  const tier =
    selectedProblem.difficulty === 'Easy' ? 1 :
    selectedProblem.difficulty === 'Medium' ? 2 : 3;

  const handleSubmit = useCallback(async () => {
    if (authState.kind !== 'authenticated') {
      // Local fallback so progress + heatmap + skill-page badges still update
      // when running without a real backend (LOCAL_DEV / signed-out demo).
      markSolved(problemKey, language);
      // xp_regular(t) = 15 × 2^(t−1) per HLD §6: 15 / 30 / 60.
      recordXpEvent(problemKey, 15 * Math.pow(2, tier - 1));
      setSubmitMsg('Saved locally (sign in to sync).');
      return;
    }
    setSubmitting(true);
    setSubmitMsg('');
    try {
      const res = await api(`/api/problems/${problemKey}/submit`, {
        method: 'POST',
        token: authState.idToken,
        body: {
          language,
          time_ms: elapsedSecs * 1000,
          hints_used: hintsRevealed,
          solution_revealed: solutionRevealed,
          tier,
          topic: selectedProblem.skillId,
        },
      });
      if (!res.ok) {
        setSubmitMsg(`Submit failed (${res.status})`);
        return;
      }
      const data = await res.json();
      if (data.first_solve) {
        setSubmitMsg(`+${data.xp_earned} XP, +${data.coins_earned} ¢`);
        markSolved(problemKey, language);
        recordXpEvent(problemKey, Number(data.xp_earned) || 0);
        void refreshUser();
      } else {
        // Still record locally so the skill page and recent-solves feed
        // reflect the solve even when the backend short-circuits rewards.
        markSolved(problemKey, language);
        setSubmitMsg('Already solved (no rewards).');
      }
    } catch (err) {
      setSubmitMsg(err instanceof Error ? err.message : 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  }, [authState, problemKey, language, elapsedSecs, hintsRevealed, solutionRevealed, tier, selectedProblem.skillId, refreshUser]);

  // ─── Hints + solution reveal (no API; the penalty is applied at submit) ───
  const allHintsRevealed = hintsRevealed >= selectedProblem.hints.length;
  const revealNextHint = useCallback(() => {
    if (allHintsRevealed) return;
    // Index reported is the hint about to be revealed (0-based) — the
    // value of `hintsRevealed` before the increment.
    track('hint_reveal', { problem: selectedProblem.id, index: hintsRevealed });
    setHintsRevealed(n => n + 1);
  }, [allHintsRevealed, selectedProblem.id, hintsRevealed]);

  // Reveal Solution: shows the reference for the current language (falling
  // back to Python if a per-language solution isn't authored yet). Costs 2
  // coins at submit time. Idempotent — clicking again is a no-op.
  const referenceSolutionFor = (lang: Language): { code: string; lang: Language } | null => {
    const ref = (selectedProblem as { referenceSolution?: { python?: string; javascript?: string; typescript?: string } }).referenceSolution;
    if (!ref) return null;
    if (lang === 'python' && ref.python) return { code: ref.python, lang: 'python' };
    if (lang === 'javascript' && ref.javascript) return { code: ref.javascript, lang: 'javascript' };
    if (lang === 'typescript' && ref.typescript) return { code: ref.typescript, lang: 'typescript' };
    // Fallback chain: prefer same family, then python as last resort.
    if (lang === 'typescript' && ref.javascript) return { code: ref.javascript, lang: 'javascript' };
    if (lang === 'javascript' && ref.typescript) return { code: ref.typescript, lang: 'typescript' };
    if (ref.python) return { code: ref.python, lang: 'python' };
    return null;
  };
  const availableSolution = referenceSolutionFor(language);
  const revealSolution = useCallback(() => {
    setSolutionRevealed(true);
  }, []);

  const consecutiveFailsRef = useRef(0);
  useEffect(() => {
    if (results.length === 0) return;
    if (allPass) consecutiveFailsRef.current = 0;
    else consecutiveFailsRef.current += 1;
  }, [results, allPass]);
  const coachState = deriveCoachState(
    results,
    running,
    consecutiveFailsRef.current,
    pyOutput?.error,
  );

  const runBtnLabel = () => {
    if (pyDisabled) return '⌛ Loading…';
    if (running)    return '⏳ Running…';
    return '▶ Run (⌘↵)';
  };

  // Latest run handler kept in a ref so keyboard / Monaco shortcuts always
  // call the current closure (which captures the up-to-date `code`).
  const triggerRun = useCallback(() => {
    if (running || pyDisabled) return;
    if (language === 'python') runPython();
    else runTests();
  }, [running, pyDisabled, language, runTests, runPython]);

  const triggerRunRef = useRef(triggerRun);
  useEffect(() => { triggerRunRef.current = triggerRun; }, [triggerRun]);

  // Window-level Cmd/Ctrl+Enter — works when focus is outside the editor.
  // Skip when typing in any regular text input outside Monaco (custom-input
  // textarea, future contact form, search box). Monaco's own bindings handle
  // the editor case directly, so we don't need to re-fire here either.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      if (!(e.metaKey || e.ctrlKey)) return;
      const ae = document.activeElement as HTMLElement | null;
      if (ae) {
        const tag = ae.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || ae.isContentEditable) return;
      }
      e.preventDefault();
      triggerRunRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // On narrow viewports, prompt and editor stack vertically (prompt on top)
  // instead of sitting side-by-side — the horizontal split is unreadable
  // on a phone-width screen.
  const isMobile = useIsMobile();

  const promptPane = (
    <div className="ide-prompt">
      <div className="sk-anno" style={{ color: 'var(--w-coral)' }}>prompt</div>
      {isAdmin && (
        <div className="ide-admin-bar">
          <button className="ide-admin-btn" onClick={() => setEditorOpen(true)}>✎ Edit (raw)</button>
          <button className="ide-admin-btn danger" onClick={handleProblemDelete}>Delete</button>
        </div>
      )}
      <Markdown className="ide-description" text={selectedProblem.description} />

      <div className="ide-anno-sub">Examples</div>
      {selectedProblem.examples.map((ex, i) => (
        <div key={i} className="ide-example">
          <div><span className="ide-label">Input:</span> <code>{ex.input}</code></div>
          <div><span className="ide-label">Output:</span> <code>{ex.output}</code></div>
          {ex.explanation && <div className="ide-explanation">{ex.explanation}</div>}
        </div>
      ))}

      {hintsRevealed > 0 && (
        <>
          <div className="ide-anno-sub">Hints</div>
          {selectedProblem.hints.slice(0, hintsRevealed).map((h, i) => (
            <div key={i} className="ide-example">
              <div className="ide-hint-row">
                <span className="ide-label">Hint {i + 1}:</span>
                <Markdown className="ide-hint-md" text={h.text} />
              </div>
            </div>
          ))}
        </>
      )}

      {solutionRevealed && availableSolution && (
        <>
          <div className="ide-anno-sub">Reference solution{availableSolution.lang !== language ? ` (${availableSolution.lang})` : ''}</div>
          <pre className="ide-reference-pre">{availableSolution.code}</pre>
        </>
      )}
    </div>
  );

  const editorPane = (
    <div className="ide-editor-col">
      {/* File tab strip — clickable for multi-file problems, decorative for single-file */}
      <div className="ide-file-tabs">
        {files.map(f => (
          <span
            key={f.name}
            className={`sk-tab ${f.name === activeFile ? 'active' : ''}`}
            style={{ fontSize: 11, cursor: files.length > 1 ? 'pointer' : 'default' }}
            onClick={() => files.length > 1 && setActiveFile(f.name)}
            title={f.readonly ? `${f.name} — read-only scaffolding. Don't edit this file.` : f.name}
          >
            {f.name}{f.readonly ? ' 🔒' : ''}
          </span>
        ))}
        <span style={{ flex: 1 }} />
        <span className="sk-pill amber" style={{ fontSize: 10 }}>{language}</span>
        <button className="sk-btn ghost" style={{ fontSize: 11 }}>format</button>
        <button
          className="sk-btn ghost"
          style={{ fontSize: 11 }}
          title="Discard your edits for this language and restore the starter"
          onClick={() => {
            const fresh = filesFromProblem(selectedProblem, language);
            setFiles(fresh);
            setActiveFile(entryFileName(fresh));
            setResults([]); setPyOutput(null);
            clearCode(selectedProblem.id, language);
          }}
        >↻ reset</button>
      </div>

      <div className="ide-editor-wrapper">
        <Editor
          height="100%"
          // `path` tells Monaco to use a separate model per file. Without it,
          // switching tabs keeps the previous file's buffer because Monaco
          // doesn't notice the value prop change as a "new file" event.
          path={activeFile}
          language={language}
          value={code}
          onChange={val => setCode(val ?? '')}
          theme="vs-dark"
          onMount={(editor, monaco) => {
            editor.addCommand(
              monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter,
              () => triggerRunRef.current(),
            );
          }}
          options={{
            minimap: { enabled: false },
            fontSize: 13,
            tabSize: 2,
            scrollBeyondLastLine: false,
            fontFamily: '"JetBrains Mono", "Fira Code", monospace',
            lineNumbersMinChars: 3,
            padding: { top: 8 },
            readOnly: activeReadonly,
            // Auto-relayout on container resize. Needed on mobile where the
            // horizontal SplitPane (whose onResize call layout()) isn't rendered.
            automaticLayout: true,
          }}
        />
      </div>
    </div>
  );

  return (
    <div className="ide-container">
      {editorOpen && (
        <ProblemEditorDrawer
          initial={selectedProblem}
          onClose={() => setEditorOpen(false)}
          onSaved={handleProblemSaved}
        />
      )}
      {/* Mobile warning — Monaco + the split panes are usable on a phone but
          cramped. Surface a one-line nudge so users on small screens know the
          editor isn't broken, just better on a laptop. */}
      {isMobile && (
        <div role="note" className="ide-mobile-banner">
          tip: the editor works on mobile but the layout is built for a wider screen.
        </div>
      )}
      {/* Sub-header */}
      <div className="ide-subheader">
        <div className="ide-subheader-left">
          <span onClick={() => navigate('/')} className="ide-breadcrumb-link">map</span>
          <span className="ide-breadcrumb-sep">›</span>
          <select
            className="ide-problem-select"
            value={problemKey}
            onChange={handleProblemChange}
          >
            <optgroup label="Algorithms">
              {algorithmProblems.map(p => (
                <option key={p.id} value={p.id}>{p.id} · {p.title}</option>
              ))}
            </optgroup>
            <optgroup label="Class Design">
              {classDesignProblems.map(p => (
                <option key={p.id} value={p.id}>{p.id} · {p.title}</option>
              ))}
            </optgroup>
            {fullstackProblems.length > 0 && (
              <optgroup label="Full-stack">
                {fullstackProblems.map(p => (
                  <option key={p.id} value={p.id}>{p.id} · {p.title}</option>
                ))}
              </optgroup>
            )}
            {agentProblems.length > 0 && (
              <optgroup label="Agents">
                {agentProblems.map(p => (
                  <option key={p.id} value={p.id}>{p.id} · {p.title}</option>
                ))}
              </optgroup>
            )}
          </select>
          <span className={`sk-pill ide-pill-sm ${selectedProblem.difficulty === 'Easy' ? 'cream' : selectedProblem.difficulty === 'Medium' ? 'amber' : 'coral'}`}>
            {selectedProblem.difficulty.toLowerCase()}
          </span>
          {(() => {
            const projected = Math.max(0, 6 - hintsRevealed - (solutionRevealed ? 2 : 0));
            return (
              <span className="sk-pill cream ide-pill-sm" title="Earn up to 6 coins per first solve. Each hint −1; revealing the solution −2.">
                +{projected} ¢ on solve
              </span>
            );
          })()}
        </div>
        <div className="ide-subheader-right">
          <span className="sk-pill ide-pill-sm">🔥 {formatTime(elapsedSecs)}</span>
          <button
            className="sk-btn ghost ide-btn-sm"
            onClick={revealNextHint}
            disabled={allHintsRevealed || selectedProblem.hints.length === 0}
            title={
              selectedProblem.hints.length === 0
                ? 'No hints for this problem'
                : allHintsRevealed
                  ? 'All hints revealed'
                  : 'Reveal next hint (−1 coin on solve)'
            }
          >
            💡 hint ({hintsRevealed}/{selectedProblem.hints.length})
          </button>
          <button
            className="sk-btn ghost ide-btn-sm"
            onClick={revealSolution}
            disabled={solutionRevealed || !availableSolution}
            title={
              !availableSolution
                ? 'No reference solution available'
                : solutionRevealed
                  ? 'Solution revealed'
                  : 'Reveal the reference solution (−2 coins on solve)'
            }
          >
            📖 {solutionRevealed ? 'solution shown' : 'reveal solution'}
          </button>
        </div>
      </div>

      <IdeLanguageTabs
        problem={selectedProblem}
        language={language}
        isAllowed={isLangAllowed}
        onPick={handleLanguageChange}
      />

      {/* Body + test pane share a vertical SplitPane (drag the seam to resize the test pane).
          Inside the body, a horizontal SplitPane lets the prompt and editor be sized independently. */}
      <SplitPane
        direction="vertical"
        storageKey="sb.split.test"
        defaultSize={160}
        anchor="second"
        min={80}
      >
        {isMobile ? (
          <div className="ide-body ide-body--stacked">
            {promptPane}
            {editorPane}
          </div>
        ) : (
          <div className="ide-body">
            <SplitPane
              direction="horizontal"
              storageKey="sb.split.body"
              defaultSize={420}
              anchor="first"
              min={220}
              onResize={() => editorRef.current?.layout()}
            >
              {promptPane}
              {editorPane}
            </SplitPane>
          </div>
        )}

      {/* Bottom: test pane — second child of the outer vertical SplitPane */}
      <IdeTestPanel
        problem={selectedProblem}
        language={language}
        results={results}
        running={running}
        pyDisabled={pyDisabled}
        pyOutput={pyOutput}
        testTab={testTab}
        setTestTab={setTestTab}
        expandedCase={expandedCase}
        setExpanded={setExpanded}
        allPass={allPass}
        passCount={passCount}
        totalCount={totalCount}
        runBtnLabel={runBtnLabel()}
        onRun={triggerRun}
        onSubmit={handleSubmit}
        submitting={submitting}
        submitMsg={submitMsg}
        customInput={customInput}
        setCustomInput={setCustomInput}
        customResult={customResult}
        onRunCustom={runCustomTest}
      />
      </SplitPane>

      {/* Coach — floats over editor; uses chosen Fizz/Buzz avatar */}
      <Coach state={coachState} />
    </div>
  );
};

// Top-level router for /problem/:problemId. React problems short-circuit to
// ReactProblemPane before AlgoOrClassCompiler's hooks run. This is the only
// hook the wrapper itself calls, so re-renders are cheap.
const CodeCompiler: React.FC = () => {
  const { problemId } = useParams<{ problemId: string }>();
  const reactProblem = problemId ? findReactProblemById(problemId) : undefined;
  if (reactProblem) return <ReactProblemPane problem={reactProblem} />;
  return <AlgoOrClassCompiler />;
};

export default CodeCompiler;
